import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import ConfirmHost from "@/components/ConfirmHost";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import TwoFactorSetup from "@/pages/TwoFactorSetup";
import TwoFactorVerify from "@/pages/TwoFactorVerify";
import Dashboard from "@/pages/Dashboard";
import Hub from "@/pages/Hub";
import PurchaseOrders from "@/pages/PurchaseOrders";
import PurchaseOrderForm from "@/pages/PurchaseOrderForm";
import PurchaseOrderRegister from "@/pages/PurchaseOrderRegister";
import SelfBills from "@/pages/SelfBills";
import SelfBillForm from "@/pages/SelfBillForm";
import SelfBillRegister from "@/pages/SelfBillRegister";
import Contacts from "@/pages/Contacts";
import Settings from "@/pages/Settings";
import Reports from "@/pages/Reports";
import ReportProfiles from "@/pages/ReportProfiles";
import ReportJobs from "@/pages/ReportJobs";
import ReportClientSummary from "@/pages/ReportClientSummary";
import ReportVehicles from "@/pages/ReportVehicles";
import ReportSubcontractorInvoices from "@/pages/ReportSubcontractorInvoices";
import Vehicles from "@/pages/Vehicles";
import SubcontractorInvoices from "@/pages/SubcontractorInvoices";
import MaintenanceRecords from "@/pages/MaintenanceRecords";
import MaintenanceSuppliers from "@/pages/MaintenanceSuppliers";
import Incidents from "@/pages/Incidents";
import D4PSDocuments from "@/pages/D4PSDocuments";
import Notes from "@/pages/Notes";
import Users from "@/pages/Users";
import PinGate from "@/pages/PinGate";
import Jobs from "@/pages/Jobs";
import JobForm from "@/pages/JobForm";
import JobCharges from "@/pages/JobCharges";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import FinanceChargeable from "@/pages/FinanceChargeable";
import FinancePayable from "@/pages/FinancePayable";
import FinanceProfit from "@/pages/FinanceProfit";
import FinanceProfitReports from "@/pages/FinanceProfitReports";
import WasteNotes from "@/pages/WasteNotes";
import WasteNoteForm from "@/pages/WasteNoteForm";
import WasteNotesCompleted from "@/pages/WasteNotesCompleted";
import WasteNotesRegister from "@/pages/WasteNotesRegister";
import ComingSoon from "@/pages/ComingSoon";
import HR from "@/pages/HR";
import HRDriverDetail from "@/pages/HRDriverDetail";
import Policies from "@/pages/Policies";

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/2fa/setup" element={<TwoFactorSetup />} />
            <Route path="/2fa/verify" element={<TwoFactorVerify />} />
            <Route path="/pin" element={<PinGate />} />

            {/* Hub — renders without sidebar (own top bar) */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Hub />
                </ProtectedRoute>
              }
            />

            {/* All other authenticated pages share the sidebar Layout */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="purchase-orders" element={<PurchaseOrders />} />
              <Route path="purchase-orders/register" element={<PurchaseOrderRegister />} />
              <Route path="purchase-orders/new" element={<PurchaseOrderForm />} />
              <Route path="purchase-orders/:id" element={<PurchaseOrderForm />} />
              <Route path="self-bills" element={<SelfBills />} />
              <Route path="self-bills/register" element={<SelfBillRegister />} />
              <Route path="self-bills/new" element={<SelfBillForm />} />
              <Route path="self-bills/:id" element={<SelfBillForm />} />
              <Route path="contacts" element={<Contacts />} />
              <Route path="vehicles" element={<Vehicles />} />
              <Route path="subcontractor-invoices" element={<SubcontractorInvoices />} />
              <Route path="vehicles/maintenance" element={<MaintenanceRecords />} />
              <Route path="vehicles/maintenance/suppliers" element={<MaintenanceSuppliers />} />
              <Route path="vehicles/incidents" element={<Incidents />} />
              <Route path="d4ps-documents" element={<D4PSDocuments />} />
              <Route path="d4ps-insurances" element={<D4PSDocuments variant="insurances" />} />
              <Route path="notes" element={<Notes />} />
              <Route path="hr" element={<HR />} />
              <Route path="hr/drivers/:id" element={<HRDriverDetail />} />
              <Route path="policies" element={<Policies />} />
              <Route path="procedures" element={<Policies variant="procedure" />} />
              <Route path="reports" element={<Reports />} />
              <Route path="reports/profiles" element={<ReportProfiles />} />
              <Route path="reports/jobs" element={<ReportJobs />} />
              <Route path="reports/client-summary" element={<ReportClientSummary />} />
              <Route path="reports/vehicles" element={<ReportVehicles />} />
              <Route path="reports/subcontractor-invoices" element={<ReportSubcontractorInvoices />} />
              <Route path="settings" element={<Settings />} />
              <Route path="settings/users" element={<Users />} />
              {/* Coming-soon module placeholders */}
              <Route path="jobs" element={<Jobs />} />
              <Route path="jobs/charges" element={<JobCharges />} />
              <Route path="jobs/new" element={<JobForm />} />
              <Route path="jobs/:id" element={<JobForm />} />
              <Route path="clients" element={<Clients />} />
              <Route path="clients/:id" element={<ClientDetail />} />
              <Route path="finance" element={<Navigate to="/finance/chargeable" replace />} />
              <Route path="finance/chargeable" element={<FinanceChargeable />} />
              <Route path="finance/payable" element={<FinancePayable />} />
              <Route path="finance/profit-reports" element={<FinanceProfitReports />} />
              <Route path="finance/profit" element={<FinanceProfit />} />
              <Route path="waste-notes" element={<WasteNotes />} />
              <Route path="waste-notes/register" element={<WasteNotesRegister />} />
              <Route path="waste-notes/completed" element={<WasteNotesCompleted />} />
              <Route path="waste-notes/new" element={<WasteNoteForm />} />
              <Route path="waste-notes/:id" element={<WasteNoteForm />} />
              {/* Legacy alias — old Customers tile redirects to Clients */}
              <Route path="customers" element={<Navigate to="/clients" replace />} />
              <Route path="suppliers" element={<Navigate to="/contacts" replace />} />
              <Route path="documents" element={<ComingSoon />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
        <ConfirmHost />
      </AuthProvider>
    </div>
  );
}

export default App;
